/**
 * Self-Evaluation Page
 *
 * Employee page for completing self-evaluation before manager assessment.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import {
  useEvaluation,
  useSelfEvaluation,
  useSaveSelfEvaluation,
  useSubmitSelfEvaluation,
} from './useEvaluations';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { CoreTaskSelfEvaluationView } from './CoreTaskSelfEvaluationView';
import type { SelfEvaluationInput } from '../../lib/evaluationsApi';

// Score labels for 1-5 scale
const scoreLabels: Record<number, string> = {
  1: 'Onvoldoende',
  2: 'Matig',
  3: 'Voldoende',
  4: 'Goed',
  5: 'Uitzonderlijk',
};

interface TaskScoreState {
  taskId: number;
  score: number | null;
  toelichting: string;
}

export function SelfEvaluationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = parseInt(id || '', 10);

  const { data: evaluation, isLoading: isLoadingEvaluation, error: evalError } = useEvaluation(evaluationId);
  const { data: selfEvalData, isLoading: isLoadingSelfEval } = useSelfEvaluation(evaluationId);
  const { data: assignedTasksData, isLoading: isLoadingTasks } = useEmployeeAssignedTasks(
    evaluation?.employeeId || 0
  );

  const saveSelfEval = useSaveSelfEvaluation();
  const submitSelfEval = useSubmitSelfEvaluation();

  const [taskScores, setTaskScores] = useState<TaskScoreState[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize task scores from existing data or assigned tasks
  useEffect(() => {
    if (assignedTasksData?.tasks && selfEvalData?.tasks) {
      const initialScores: TaskScoreState[] = assignedTasksData.tasks.map((task) => {
        const existingScore = selfEvalData.tasks.find((s) => s.taskId === task.id);
        return {
          taskId: task.id,
          score: existingScore?.score ?? null,
          toelichting: existingScore?.toelichting || '',
        };
      });
      setTaskScores(initialScores);
    } else if (assignedTasksData?.tasks) {
      const initialScores: TaskScoreState[] = assignedTasksData.tasks.map((task) => ({
        taskId: task.id,
        score: null,
        toelichting: '',
      }));
      setTaskScores(initialScores);
    }
  }, [assignedTasksData, selfEvalData]);

  const handleScoreChange = (taskId: number, score: number) => {
    setTaskScores((prev) =>
      prev.map((ts) => (ts.taskId === taskId ? { ...ts, score } : ts))
    );
    setHasUnsavedChanges(true);
  };

  const handleToelichtingChange = (taskId: number, toelichting: string) => {
    setTaskScores((prev) =>
      prev.map((ts) => (ts.taskId === taskId ? { ...ts, toelichting } : ts))
    );
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    const validScores: SelfEvaluationInput[] = taskScores
      .filter((ts) => ts.score !== null)
      .map((ts) => ({
        taskId: ts.taskId,
        score: ts.score!,
        toelichting: ts.toelichting || undefined,
      }));

    if (validScores.length === 0) {
      alert('Vul minimaal één score in voordat je opslaat.');
      return;
    }

    try {
      await saveSelfEval.mutateAsync({ evaluationId, tasks: validScores });
      setHasUnsavedChanges(false);
    } catch {
      // Error handled by mutation state
    }
  };

  const handleSubmit = async () => {
    // Check all tasks are scored
    const unscored = taskScores.filter((ts) => ts.score === null);
    if (unscored.length > 0) {
      alert(`Je hebt nog ${unscored.length} taak/taken niet beoordeeld.`);
      return;
    }

    // Save first
    const validScores: SelfEvaluationInput[] = taskScores.map((ts) => ({
      taskId: ts.taskId,
      score: ts.score!,
      toelichting: ts.toelichting || undefined,
    }));

    try {
      await saveSelfEval.mutateAsync({ evaluationId, tasks: validScores });
      await submitSelfEval.mutateAsync(evaluationId);
      navigate('/dashboard');
    } catch {
      // Error handled by mutation state
    }
  };

  const isLoading = isLoadingEvaluation || isLoadingSelfEval || isLoadingTasks;
  const completedCount = taskScores.filter((ts) => ts.score !== null).length;
  const totalCount = taskScores.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isCompleted = evaluation?.selfEvaluationAt !== null;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Data laden...</span>
        </div>
      </Layout>
    );
  }

  if (evalError || !evaluation) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">Beoordeling niet gevonden.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Terug naar dashboard
          </button>
        </div>
      </Layout>
    );
  }

  // Epic 4 (Story 4.5, AC #1, AD-12): core_tasks_v2 zelfevaluatie krijgt een
  // eigen kerntaak-kaartpatroon (CoreTaskSelfEvaluationView) i.p.v. de
  // taken-lijst hieronder — dispatch op `scoring_model`, zelfde patroon als
  // de wizard (Story 4.2).
  if (evaluation.scoringModel === 'core_tasks_v2') {
    return (
      <Layout>
        <CoreTaskSelfEvaluationView evaluationId={evaluationId} onSubmitted={() => navigate('/dashboard')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="self-evaluation-page" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Zelf-evaluatie</h1>
          <p className="mt-1 text-sm text-gray-500">
            Beoordeel je eigen prestaties voor de periode{' '}
            {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL')} -{' '}
            {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL')}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Voortgang</span>
            <span className="text-sm text-gray-500">
              {completedCount} van {totalCount} taken beoordeeld
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {isCompleted && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-700">
              Je zelf-evaluatie is ingediend op{' '}
              {new Date(evaluation.selfEvaluationAt!).toLocaleDateString('nl-NL')}.
            </p>
          </div>
        )}

        {/* Task list */}
        <div className="space-y-4 mb-6">
          {assignedTasksData?.tasks?.map((task) => {
            const taskScore = taskScores.find((ts) => ts.taskId === task.id);
            return (
              <div
                key={task.id}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{task.name}</h3>
                    {task.beschrijving && (
                      <p className="mt-1 text-sm text-gray-500">{task.beschrijving}</p>
                    )}
                  </div>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                    {task.weging}% weging
                  </span>
                </div>

                {/* Score selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jouw beoordeling
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {[1, 2, 3, 4, 5, 6].map((score) => (
                      <button
                        key={score}
                        onClick={() => handleScoreChange(task.id, score)}
                        disabled={isCompleted}
                        className={`px-4 py-2 rounded-lg border transition-colors ${
                          taskScore?.score === score
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        } ${isCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="font-medium">{score}</span>
                        <span className="hidden sm:inline ml-1 text-xs">
                          - {scoreLabels[score]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label
                    htmlFor={`toelichting-${task.id}`}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Toelichting (optioneel)
                  </label>
                  <textarea
                    id={`toelichting-${task.id}`}
                    rows={2}
                    value={taskScore?.toelichting || ''}
                    onChange={(e) => handleToelichtingChange(task.id, e.target.value)}
                    disabled={isCompleted}
                    placeholder="Geef een toelichting op je score..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        {!isCompleted && (
          <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSave}
                disabled={saveSelfEval.isPending || !hasUnsavedChanges}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {saveSelfEval.isPending ? 'Opslaan...' : 'Tussentijds Opslaan'}
              </button>
              {hasUnsavedChanges && (
                <span className="text-sm text-yellow-600">Niet-opgeslagen wijzigingen</span>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitSelfEval.isPending || completedCount < totalCount}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitSelfEval.isPending ? 'Indienen...' : 'Zelf-evaluatie Indienen'}
            </button>
          </div>
        )}

        {/* Error messages */}
        {(saveSelfEval.error || submitSelfEval.error) && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              Er is een fout opgetreden. Probeer het opnieuw.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
